import { IUserLoginPayload } from "../../../types/api/user";
import { useUserLogin } from "../../../hooks/api/user/hooks";
import { Button, Col, Form, Input, message, Row } from "antd";
import {
  LockOutlined,
  MailOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useUserStore } from "../../../store/slices/user";
import { useEffect, useState } from "react";
import axiosInstance from "../../../lib/axios";
import {
  adminForgotPassword,
  adminResetPassword,
} from "../../../hooks/api/user/callbacks";

import { useMessageApi } from "../../../provider/AntdNotification";

// No self-service password reset existed for admin accounts at all
// before this - the 4 named logins had only their one-time temp
// password (Oliver's testing doc, round 4). This app has no real
// router (App.tsx renders <Login /> for every unauthenticated path),
// so "forgot" and "reset" are extra states here, the same way the
// existing OTP step already works, rather than separate routes.
type LoginStep = "credentials" | "otp" | "forgot" | "forgot-sent" | "reset" | "reset-done";

export default function Login() {
  const [form] = Form.useForm();
  const [otpForm] = Form.useForm();
  const [forgotForm] = Form.useForm();
  const [resetForm] = Form.useForm();
  const messageNotifier = useMessageApi();
  const navigate = useNavigate();
  const [step, setStep] = useState<LoginStep>("credentials");
  const [resetParams, setResetParams] = useState<{ token: string; email: string } | null>(null);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const { updateToken } = useUserStore();

  // A reset-password link (?token=...&email=...) lands here directly -
  // detected on mount since there's no dedicated route for it.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    const email = params.get("email");
    if (token && email) {
      setResetParams({ token, email });
      setStep("reset");
    }
  }, []);

  const { mutate, isPending } = useUserLogin({
    onSuccess: (res: any) => {
      const responseData = res.data?.data || res.data; // fallback for both structures
      updateToken({
        token: responseData.accessToken || responseData.token,
        user: responseData.userInfo || responseData.user,
      });
      messageNotifier.success("Logged in successfully");
      navigate("/");
    },

    onError: (err: any) => {
      messageNotifier.error(err.message || "Login failed");
    },
  });

  return (
    <div className="h-screen w-full flex overflow-hidden">
      {/* LEFT BRAND PANEL */}
      <div className="hidden md:flex w-1/2 bg-slate-950 relative items-center justify-center overflow-hidden">
        <div className="relative z-10 text-white text-center px-10">
          <div className="flex items-center justify-center mb-6">
            <img
              src="/dark_logo.png"
              className="w-50 h-20 object-contain"
              alt="ArtPark Logo"
            />
          </div>

          <p className="text-slate-300 mt-4 text-sm leading-relaxed max-w-md font-light">
            Discover top-quality contemporary sculpture curated for collectors,
            designers, luxury interiors, and extraordinary landscapes.
          </p>

          <div className="mt-8 text-xs text-slate-400 tracking-wide uppercase font-medium">
            Contemporary Sculptures • Curated Collections • Global Artists
          </div>
        </div>
      </div>

      {/* RIGHT PANEL */}
      <div className="relative flex w-full md:w-1/2 items-center justify-center overflow-hidden bg-slate-100">
        {/* Background Image */}
        <img
          src="/logo.png"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-5"
        />

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/90 via-slate-50/95 to-blue-50/90" />

        <div className="relative z-10 w-full max-w-md px-6">
          <div className="bg-white/90 backdrop-blur-xl shadow-2xl rounded-3xl p-8 border border-white/40">
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <div className="w-[150px] h-15  flex items-center justify-center ">
                {step === "otp" ? (
                  <SafetyCertificateOutlined className="text-white text-2xl" />
                ) : (
                  <img
                    src="/logo.png"
                    alt="logo"
                    className="w-full h-full object-contain"
                  />
                )}
              </div>
            </div>

            {step === "credentials" ? (
              <>
                <h2 className="text-center text-2xl font-semibold text-slate-800">
                  Sign in to ArtPark Admin
                </h2>

                <p className="text-center text-sm text-slate-500 mt-2 mb-7">
                  Access your operations dashboard
                </p>

                <Form
                  form={form}
                  layout="vertical"
                  size="large"
                  onFinish={(values: IUserLoginPayload) => mutate(values)}
                >
                  <Row gutter={[0, 8]}>
                    <Col span={24}>
                      <Form.Item
                        name="email"
                        rules={[
                          { required: true, message: "Email is required" },
                        ]}
                      >
                        <Input
                          placeholder="Admin Email"
                          prefix={<MailOutlined />}
                          className="rounded-xl h-12"
                        />
                      </Form.Item>
                    </Col>

                    <Col span={24}>
                      <Form.Item
                        name="password"
                        rules={[
                          { required: true, message: "Password is required" },
                        ]}
                      >
                        <Input.Password
                          placeholder="Password"
                          prefix={<LockOutlined />}
                          className="rounded-xl h-12"
                        />
                      </Form.Item>
                    </Col>

                    <Col span={24}>
                      <Button
                        htmlType="submit"
                        loading={isPending}
                        className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-medium rounded-xl border-none shadow-lg"
                      >
                        Login to Dashboard
                      </Button>
                    </Col>

                    <Col span={24} className="text-center">
                      <button
                        type="button"
                        onClick={() => setStep("forgot")}
                        className="text-sm text-slate-500 hover:text-slate-800 underline underline-offset-2"
                      >
                        Forgot password?
                      </button>
                    </Col>
                  </Row>
                </Form>
              </>
            ) : step === "forgot" ? (
              <>
                <h2 className="text-center text-2xl font-semibold text-slate-800">
                  Reset your password
                </h2>
                <p className="text-center text-sm text-slate-500 mt-2 mb-7">
                  Enter your admin email and we'll send you a reset link
                </p>

                <Form
                  form={forgotForm}
                  layout="vertical"
                  size="large"
                  onFinish={async (values: { email: string }) => {
                    setForgotLoading(true);
                    try {
                      await adminForgotPassword(values.email);
                      setStep("forgot-sent");
                    } catch (err: any) {
                      messageNotifier.error(
                        err?.response?.data?.message || err?.message || "Failed to send reset email",
                      );
                    } finally {
                      setForgotLoading(false);
                    }
                  }}
                >
                  <Form.Item
                    name="email"
                    rules={[
                      { required: true, message: "Email is required" },
                      { type: "email", message: "Enter a valid email" },
                    ]}
                  >
                    <Input
                      placeholder="Admin Email"
                      prefix={<MailOutlined />}
                      className="rounded-xl h-12"
                      autoFocus
                    />
                  </Form.Item>

                  <div className="flex gap-3 mt-2">
                    <Button
                      onClick={() => setStep("credentials")}
                      className="flex-1 h-12 rounded-xl border-slate-200 text-slate-600"
                    >
                      Back
                    </Button>
                    <Button
                      htmlType="submit"
                      loading={forgotLoading}
                      className="flex-1 h-12 bg-slate-900 hover:bg-slate-800 text-white font-medium rounded-xl border-none"
                    >
                      Send reset link
                    </Button>
                  </div>
                </Form>
              </>
            ) : step === "forgot-sent" ? (
              <>
                <h2 className="text-center text-2xl font-semibold text-slate-800">
                  Check your email
                </h2>
                <p className="text-center text-sm text-slate-500 mt-2 mb-7">
                  If an account exists with that email, a reset link has been sent. It expires in 1 hour.
                </p>
                <Button
                  onClick={() => setStep("credentials")}
                  className="w-full h-12 rounded-xl border-slate-200 text-slate-600"
                >
                  Back to login
                </Button>
              </>
            ) : step === "reset" ? (
              <>
                <h2 className="text-center text-2xl font-semibold text-slate-800">
                  Set a new password
                </h2>
                <p className="text-center text-sm text-slate-500 mt-2 mb-7">
                  {resetParams?.email}
                </p>

                <Form
                  form={resetForm}
                  layout="vertical"
                  size="large"
                  onFinish={async (values: { password: string; confirmPassword: string }) => {
                    if (!resetParams) return;
                    if (values.password !== values.confirmPassword) {
                      messageNotifier.error("Passwords don't match");
                      return;
                    }
                    setResetLoading(true);
                    try {
                      await adminResetPassword({
                        email: resetParams.email,
                        token: resetParams.token,
                        password: values.password,
                      });
                      setStep("reset-done");
                    } catch (err: any) {
                      messageNotifier.error(
                        err?.response?.data?.message || err?.message || "Failed to reset password",
                      );
                    } finally {
                      setResetLoading(false);
                    }
                  }}
                >
                  <Form.Item
                    name="password"
                    rules={[
                      { required: true, message: "New password is required" },
                      { min: 8, message: "At least 8 characters" },
                    ]}
                  >
                    <Input.Password
                      placeholder="New password"
                      prefix={<LockOutlined />}
                      className="rounded-xl h-12"
                      autoFocus
                    />
                  </Form.Item>
                  <Form.Item
                    name="confirmPassword"
                    rules={[{ required: true, message: "Confirm your new password" }]}
                  >
                    <Input.Password
                      placeholder="Confirm new password"
                      prefix={<LockOutlined />}
                      className="rounded-xl h-12"
                    />
                  </Form.Item>

                  <Button
                    htmlType="submit"
                    loading={resetLoading}
                    className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-medium rounded-xl border-none"
                  >
                    Reset password
                  </Button>
                </Form>
              </>
            ) : step === "reset-done" ? (
              <>
                <h2 className="text-center text-2xl font-semibold text-slate-800">
                  Password reset
                </h2>
                <p className="text-center text-sm text-slate-500 mt-2 mb-7">
                  Your password has been changed. You can now log in.
                </p>
                <Button
                  onClick={() => {
                    window.history.replaceState({}, "", window.location.pathname);
                    setResetParams(null);
                    setStep("credentials");
                  }}
                  className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-medium rounded-xl border-none text-white"
                >
                  Back to login
                </Button>
              </>
            ) : (
              <>
                <h2 className="text-center text-2xl font-semibold text-slate-800">
                  Two-Factor Authentication
                </h2>

                <p className="text-center text-sm text-slate-500 mt-2 mb-7">
                  Enter the 6-digit code from your authenticator app
                </p>

                <Form form={otpForm} layout="vertical" size="large">
                  <Form.Item
                    name="otp_token"
                    rules={[
                      { required: true, message: "OTP is required" },
                      { len: 6, message: "Must be 6 digits" },
                      { pattern: /^\d{6}$/, message: "Digits only" },
                    ]}
                  >
                    <Input
                      placeholder="000000"
                      maxLength={6}
                      className="rounded-xl h-12 text-center text-2xl tracking-[0.5em] font-mono"
                      autoFocus
                    />
                  </Form.Item>

                  <div className="flex gap-3 mt-2">
                    <Button
                      onClick={() => {
                        setStep("credentials");
                        otpForm.resetFields();
                      }}
                      className="flex-1 h-12 rounded-xl border-slate-200 text-slate-600"
                    >
                      Back
                    </Button>
                  </div>
                </Form>
              </>
            )}

            <div className="text-center text-xs text-slate-400 mt-6">
              ArtPark © {new Date().getFullYear()} • Contemporary Sculpture
              Portal
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
